import * as braze from '@braze/web-sdk';
import type { ProductPageMarketingEventData } from 'types/capi/pdp';
import type { ProductSubType } from 'types/capi/shared';

const MOBILE_SCREEN_BREAKPOINT = 768;

type AffiliateViewedEventData = {
  imageUrl?: string;
  productId?: number;
  productName: string;
  productType?: ProductSubType;
  productUrl?: string;
  retailerName: string;
};

let IS_INITIALIZED = false;
let HAS_ACCOUNT: boolean | undefined;
let USER_ID: string | undefined;
let IDENTIFIED_USER_ID: string | undefined;

function isSuppressed() {
  return window.env?.BRAZE_SUPPRESSED === true;
}

function init() {
  if (IS_INITIALIZED) return;

  if (isSuppressed()) return;

  const apiKey = window.env?.BRAZE_SDK_API_KEY;
  const baseUrl = window.env?.BRAZE_BASE_URL;

  if (!apiKey || !baseUrl) return;

  braze.initialize(apiKey, {
    allowUserSuppliedJavascript: true,
    baseUrl,
    enableLogging: false
  });

  IS_INITIALIZED = true;

  applyIdentity();
  braze.automaticallyShowInAppMessages();
  applyAccountStatus();
  braze.openSession();
}

function logEvent(name: string, props: object) {
  if (!IS_INITIALIZED) init();
  if (!IS_INITIALIZED) return;

  braze.logCustomEvent(name, props);
}

function updateProductViews(productName: string) {
  if (!IS_INITIALIZED) init();
  if (!IS_INITIALIZED) return;

  const user = braze.getUser();

  if (!user) return;

  user.addToCustomAttributeArray('viewed_products', productName);
}

function hasAccount() {
  return HAS_ACCOUNT ?? window.env?.BRAZE_HAS_ACCOUNT === true;
}

function applyAccountStatus() {
  const user = braze.getUser();

  if (!user) return;
  if (!hasAccount() && user.getUserId()) return;

  user.setCustomUserAttribute('has_account', hasAccount());
}

function getProductCategory(productType?: ProductSubType) {
  if (productType === 'sneaker') return 'Footwear';
  if (productType === 'clothing') return 'Clothing';
}

function getDeviceType() {
  return window.screen.width < MOBILE_SCREEN_BREAKPOINT ? 'Mobile' : 'Desktop';
}

function enableTracking() {
  braze.enableSDK();
}

function disableTracking() {
  braze.disableSDK();
}

function logPdpViewEvent(eventData: ProductPageMarketingEventData) {
  updateProductViews(eventData.properties['Product Name']);

  return logEvent(eventData.event_name, {
    ...eventData.properties,
    'Device Type': getDeviceType()
  });
}

function logArticleViewEvent(title: string) {
  return logEvent('News', {
    'Device Type': getDeviceType(),
    Headline: title,
    Source: 'Web'
  });
}

function logLoginEvent() {
  return logEvent('Login', {
    'Device Type': getDeviceType(),
    'Email Consent Granted': true,
    'Mobile Consent Granted': false,
    Source: 'Web'
  });
}

function logAffiliateViewedEvent({
  productName,
  retailerName,
  productType,
  productUrl,
  imageUrl,
  productId
}: AffiliateViewedEventData) {
  return logEvent('Affiliate Viewed', {
    'Affilaite Link': productUrl,
    Category: getProductCategory(productType),
    'Image URL': imageUrl,
    'Product ID': productId?.toString(),
    'Product Name': productName,
    'Retailer Name': retailerName,
    Source: 'Web'
  });
}

function logPurchaseEvent({
  name,
  price,
  meta_data
}: {
  meta_data?: object;
  name: string;
  price: number;
}) {
  if (!IS_INITIALIZED) init();
  if (!IS_INITIALIZED) return;

  braze.logPurchase(name, price, 'GBP', 1, meta_data);
}

function isFeatureFlagActive(name: string) {
  return Boolean(braze.getFeatureFlag(name)?.enabled);
}

function requestPushPermission() {
  if (braze?.isPushSupported()) braze.requestPushPermission();
}

function getBanner(placementId: string) {
  if (!IS_INITIALIZED) return;

  return braze.getBanner(placementId);
}

function insertBanner(
  banner: Parameters<typeof braze.insertBanner>[0],
  container: HTMLElement
) {
  if (!IS_INITIALIZED) return;

  braze.insertBanner(banner, container);
}

function subscribeToBannersUpdates(callback: (banners: unknown) => void) {
  if (!IS_INITIALIZED) return;

  return braze.subscribeToBannersUpdates(callback);
}

function removeSubscription(subscriptionId: string) {
  if (!IS_INITIALIZED) return;

  braze.removeSubscription(subscriptionId);
}

function requestBannersRefresh(placementIds: string[]) {
  if (!IS_INITIALIZED) return;

  braze.requestBannersRefresh(placementIds);
}

function applyIdentity() {
  if (!USER_ID || USER_ID === IDENTIFIED_USER_ID) return;

  IDENTIFIED_USER_ID = USER_ID;

  applyAccountStatus();
  braze.changeUser(USER_ID);
  applyAccountStatus();
}

function setUser(uid: string) {
  USER_ID = uid;
  HAS_ACCOUNT = true;

  if (!IS_INITIALIZED) return init();

  applyIdentity();
  braze.openSession();
}

export default {
  disableTracking,
  enableTracking,
  getBanner,
  init,
  insertBanner,
  isFeatureFlagActive,
  logAffiliateViewedEvent,
  logArticleViewEvent,
  logEvent,
  logLoginEvent,
  logPdpViewEvent,
  logPurchaseEvent,
  removeSubscription,
  requestBannersRefresh,
  requestPushPermission,
  setUser,
  subscribeToBannersUpdates
};
